/**
 * Knocked Out Panel
 * Replaces the betting controls once you've run out of chips while the game
 * goes on: go to the lobby, start a new game (a solo game: a fresh one
 * against the bots), or spectate (keep watching without playing — with no
 * other human left in, the game then deals itself). While spectating it
 * becomes a small banner with the same ways out.
 */

import React, { useState } from 'react';

interface KnockedOutPanelProps {
  /** Where you finished (2 = runner-up), if known. */
  place: number | null;
  spectating: boolean;
  /** Leave the table for the lobby. */
  onLobby: () => Promise<void>;
  /** Solo games only: leave and start a fresh solo game. */
  onNewGame?: () => Promise<void>;
  onSpectate: () => Promise<void>;
}

const ordinal = (n: number) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

type Busy = 'lobby' | 'new' | 'spectate' | null;

const KnockedOutPanel: React.FC<KnockedOutPanelProps> = ({ place, spectating, onLobby, onNewGame, onSpectate }) => {
  const [busy, setBusy] = useState<Busy>(null);

  const run = (which: Exclude<Busy, null>, action: () => Promise<void>) => async () => {
    setBusy(which);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  const button = (which: 'lobby' | 'new', label: string, busyLabel: string, action: () => Promise<void>, className: string) => (
    <button
      data-testid={which === 'lobby' ? 'leave-table' : 'new-solo-game'}
      onClick={run(which, action)}
      disabled={busy !== null}
      className={`flex-1 py-3 disabled:opacity-60 text-white font-bold rounded-xl transition-all ${className}`}
    >
      {busy === which ? busyLabel : label}
    </button>
  );
  const lobbyButton = button('lobby', 'Lobby', 'Leaving…', onLobby, 'bg-gray-700 hover:bg-gray-600');
  const newGameButton = onNewGame && button('new', 'New game', 'Starting…', onNewGame, 'bg-emerald-700 hover:bg-emerald-600');

  if (spectating) {
    return (
      <div
        data-testid="spectating"
        className="p-3 rounded-xl border border-gray-700 bg-gray-900/70 flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">👀 Spectating</div>
          <div className="text-[11px] text-gray-400">
            {place ? `You finished ${ordinal(place)}. ` : ''}The game plays on without you.
          </div>
        </div>
        <div className={`${onNewGame ? 'w-48' : 'w-28'} flex gap-2 text-sm`}>
          {newGameButton}
          {lobbyButton}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="knocked-out" className="p-3 sm:p-4 rounded-xl border border-gray-700 bg-gray-900/70 text-center space-y-2">
      <div className="text-3xl">🪙</div>
      <div className="text-lg sm:text-xl font-bold text-white">You're out of chips</div>
      <div className="text-xs sm:text-sm text-gray-400">
        {place ? `You finished ${ordinal(place)}. ` : ''}
        {onNewGame ? 'Start a new game, go to the lobby, or stay and watch.' : 'Go to the lobby, or stay and watch the rest of the game.'}
      </div>
      <div className="flex gap-2">
        {newGameButton}
        {lobbyButton}
        <button
          data-testid="spectate"
          onClick={run('spectate', onSpectate)}
          disabled={busy !== null}
          className="flex-1 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 disabled:opacity-60 text-white font-bold rounded-xl shadow-lg transition-all"
        >
          {busy === 'spectate' ? 'Joining…' : 'Spectate'}
        </button>
      </div>
    </div>
  );
};

export default KnockedOutPanel;
